import { Injectable, inject } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  CurrencyConversionGQL,
  CurrencyConversionQuery,
  CurrencyConversionQueryVariables,
  CurrencyConversion,
  LatestCurrencyConversionQuery,
  LatestCurrencyConversionGQL,
  CurrencyConversionByIdGQL,
  CurrencyConversionByIdQueryVariables,
  CurrencyConversionByIdQuery,
} from '@app/generated/graphql';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ToastService } from '../toast/toast.service';
import { BigDecimal, RoundingMode } from 'bigdecimal';

@Injectable({
  providedIn: 'root',
})
export class ExchangeRateService {
  private apollo = inject(Apollo);
  private currencyConversionByIdGql = inject(CurrencyConversionByIdGQL);
  private currencyConversionGql = inject(CurrencyConversionGQL);
  private latestCurrencyConversionGql = inject(LatestCurrencyConversionGQL);
  private toastService = inject(ToastService);

  constructor() {}

  public getExchangeRate(
    variables: CurrencyConversionQueryVariables,
  ): Observable<CurrencyConversion> {
    return this.apollo
      .watchQuery<CurrencyConversionQuery>({
        query: this.currencyConversionGql.document,
        variables: variables,
        fetchPolicy: 'no-cache', // always want to ensure we have the latest data
      })
      .valueChanges.pipe(
        map(
          (res: ApolloQueryResult<CurrencyConversionQuery>) =>
            res.data.currencyConversion,
        ),
      );
  }

  public getLatestExchangeRate(): Observable<CurrencyConversion> {
    return this.apollo
      .watchQuery<LatestCurrencyConversionQuery>({
        query: this.latestCurrencyConversionGql.document,
        fetchPolicy: 'no-cache', // always want to ensure we have the latest data
      })
      .valueChanges.pipe(
        map(
          (res: ApolloQueryResult<LatestCurrencyConversionQuery>) =>
            res.data.latestCurrencyConversion,
        ),
      );
  }

  public getCurrencyConversion({
    fromCurrency,
    toCurrency,
    currencyConversion,
  }: {
    fromCurrency: string;
    toCurrency: string;
    currencyConversion: CurrencyConversion;
  }): BigDecimal | null {
    if (!fromCurrency || !toCurrency) {
      this.toastService.showErrorToast(
        `Invalid currency conversion from (${fromCurrency}) to (${toCurrency})`,
      );

      return null;
    }

    if (!currencyConversion) {
      this.toastService.showErrorToast(
        `No currency conversion found for (${fromCurrency}) to (${toCurrency})`,
      );

      return null;
    }

    if (fromCurrency === toCurrency) {
      return new BigDecimal('1');
    }
    // examples will all make use of
    // eg. USD to CAD

    // standard conversion
    // fromCurrency to toCurrency
    // eg. USD to CAD
    const directConversion: BigDecimal | null = this.directConversion(
      fromCurrency,
      toCurrency,
      currencyConversion,
    );
    if (directConversion) {
      return directConversion;
    }

    // inverse conversion
    // toCurrency to fromCurrency
    // eg. CAD to USD
    const inverseConversion: BigDecimal | null = this.inverseConversion(
      fromCurrency,
      toCurrency,
      currencyConversion,
    );
    if (inverseConversion) {
      return inverseConversion;
    }

    // zar triangulation
    // fromCurrency to ZAR && toCurrency to ZAR
    // eg. CAD to USD
    // check if we have CADZAR and USDZAR
    // check if we have ZARCAD and ZARUSD
    const zarTriangulation: BigDecimal | null = this.zarTriangulation(
      fromCurrency,
      toCurrency,
      currencyConversion,
    );
    if (zarTriangulation) {
      return zarTriangulation;
    }

    return null;
  }

  public getCurrencyConversionById(
    currencyConversionId: string,
  ): Observable<CurrencyConversion> {
    const variables: CurrencyConversionByIdQueryVariables = {
      currencyConversionId: currencyConversionId,
    };
    return this.apollo
      .watchQuery<CurrencyConversionByIdQuery>({
        query: this.currencyConversionByIdGql.document,
        variables: variables,
        fetchPolicy: 'no-cache', // always want to ensure we have the latest data
      })
      .valueChanges.pipe(
        map(
          (res: ApolloQueryResult<CurrencyConversionQuery>) =>
            res.data.currencyConversion,
        ),
      );
  }

  private directConversion(
    fromCurrency: string,
    toCurrency: string,
    currencyConversion: CurrencyConversion,
  ): BigDecimal | null {
    const exchangeCurrency: string = `${fromCurrency}${toCurrency}`;
    if (currencyConversion.rates[exchangeCurrency]) {
      return new BigDecimal(String(currencyConversion.rates[exchangeCurrency]));
    }

    return null;
  }

  private inverseConversion(
    fromCurrency: string,
    toCurrency: string,
    currencyConversion: CurrencyConversion,
  ): BigDecimal | null {
    const inverseExchangeCurrency: string = `${toCurrency}${fromCurrency}`;
    if (currencyConversion.rates[inverseExchangeCurrency]) {
      const inverseExchangeRate = new BigDecimal(
        String(currencyConversion.rates[inverseExchangeCurrency]),
      );
      const one: BigDecimal = new BigDecimal('1');
      return one.divide(
        inverseExchangeRate,
        25, // 25 decimal places https://github.com/iriscouch/bigdecimal.js
        RoundingMode.HALF_EVEN(),
      );
    }

    return null;
  }

  private zarTriangulation(
    fromCurrency: string,
    toCurrency: string,
    currencyConversion: CurrencyConversion,
  ): BigDecimal | null {
    // check if we have currencyToZAR rates (e.g., USDZAR and EURZAR)
    const fromCurrencyToZarRate =
      currencyConversion.rates[`${fromCurrency}ZAR`];
    const toCurrencyToZarRate = currencyConversion.rates[`${toCurrency}ZAR`];
    if (fromCurrencyToZarRate && toCurrencyToZarRate) {
      // USDZAR : EURZAR => USDEUR = USDZAR ÷ EURZAR
      return new BigDecimal(String(fromCurrencyToZarRate)).divide(
        new BigDecimal(String(toCurrencyToZarRate)),
        25, // 25 decimal places https://github.com/iriscouch/bigdecimal.js
        RoundingMode.HALF_EVEN(),
      );
    }

    // check if we have ZARtoCurrency rates (e.g., ZARUSD and ZAREUR)
    const zarToFromCurrencyRate =
      currencyConversion.rates[`ZAR${fromCurrency}`];
    const zarToToCurrencyRate = currencyConversion.rates[`ZAR${toCurrency}`];
    if (zarToFromCurrencyRate && zarToToCurrencyRate) {
      // ZARUSD : ZAREUR => USDEUR = ZAREUR ÷ ZARUSD
      return new BigDecimal(String(zarToToCurrencyRate)).divide(
        new BigDecimal(String(zarToFromCurrencyRate)),
        25, // 25 decimal places https://github.com/iriscouch/bigdecimal.js
        RoundingMode.HALF_EVEN(),
      );
    }

    return null;
  }
}
